import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, Stack } from "@altimateai/lego";
import { useQuery } from "@tanstack/react-query";
import { getTableauProjects } from "@/helpers/apis";
import { ComponentLoader } from "@/components/loader";
import { TableauProject } from "./types";
import { DatastoreStepHeader } from "./DatastoreStepHeader";
import { ProjectAlerts } from "./TableauProjectPicker/ProjectAlerts";
import { ProjectLocationSwitch } from "./TableauProjectPicker/ProjectLocationSwitch";
import { ProjectTreeSection } from "./TableauProjectPicker/ProjectTreeSection";
import { ProjectInfoDisplay } from "./TableauProjectPicker/ProjectInfoDisplay";
import { ProjectActionButtons } from "./TableauProjectPicker/ProjectActionButtons";
import {
  buildProjectTree,
  useWorkbookPickerState,
} from "./TableauProjectPicker/useWorkbookPickerState";

const DEFAULT_PROJECT_NAME = "altimate_workbooks";
const SUBPROJECT_NAME = "altimate_workbooks";

interface TableauProjectPickerProps {
  credentials: {
    url: string;
    site_name?: string;
    // PAT becomes optional when ``datastore_id`` is provided — backend then
    // falls back to credentials stored on that data store. Used by the
    // detail-page "Add database" modal when the user keeps the existing PAT.
    pat_name?: string;
    pat_secret?: string;
    datastore_id?: number;
    // Sibling site name (in the same instance as ``datastore_id``) whose
    // PAT should be reused. Backend resolves via instance_id + site_name.
    source_site_name?: string;
  };
  /** Whether the PAT role can publish workbooks. Defaults to ``true`` for
   * backward compatibility when the backend doesn't surface the role. When
   * ``false`` (Site Administrator Explorer), the picker switches to "locate"
   * mode: the customer must have pre-uploaded the ``altimate_workbooks``
   * subproject under the chosen parent. */
  canPublishWorkbooks?: boolean;
  /** Pending state of the parent's create-datastore mutation. Drives the
   * Finish button spinner so it reflects the actual server round-trip rather
   * than a local sync flag that never clears on async failure. */
  isFinishing?: boolean;
  handleBack: () => void;
  handleFinish: (selectedProject: TableauProject) => void;
}

const TableauProjectPicker = ({
  credentials,
  canPublishWorkbooks = true,
  isFinishing = false,
  handleBack,
  handleFinish,
}: TableauProjectPickerProps): JSX.Element => {
  const [selectedProject, setSelectedProject] = useState<TableauProject | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [useCustomLocation, setUseCustomLocation] = useState(false);

  // Matches the backend's `tableau.com` host convention
  // (`Datastore.tsx::isTableauCloud`, `tableau_workbook_uploader.py:40`).
  const isTableauCloud = credentials.url.toLowerCase().includes("tableau.com");

  // Fetch projects
  const {
    data: projects = [],
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: [
      "tableau-projects",
      credentials.url,
      credentials.site_name,
      credentials.datastore_id,
      credentials.source_site_name,
    ],
    queryFn: () => getTableauProjects(credentials),
    enabled: !!(
      credentials.url &&
      ((credentials.pat_name && credentials.pat_secret) || credentials.datastore_id)
    ),
    retry: 1,
  });

  const {
    tableauDefaultProject,
    mustUseCustomLocation,
    disabledProjectIds,
    selectedProjectHasAltimateWorkbooks,
  } = useWorkbookPickerState({ projects, selectedProject, canPublishWorkbooks });

  // Set custom location mode based on default project state
  useEffect(() => {
    if (mustUseCustomLocation) {
      setUseCustomLocation(true);
    }
  }, [mustUseCustomLocation]);

  const handleSave = () => {
    // Spinner state is now driven by the parent's mutation pending flag
    // (``isFinishing``) so it correctly clears on async failure too.
    if (!useCustomLocation) {
      handleFinish(tableauDefaultProject ?? { id: "default", name: "Default", path: "Default" });
    } else if (selectedProject) {
      handleFinish(selectedProject);
    }
  };

  // Determine if save button should be enabled
  const canSave = useMemo(() => {
    // Cannot save if there's an API error or still loading
    if (error || isLoading) {
      return false;
    }

    if (!useCustomLocation) {
      // Default location:
      // - Creator: can save if default project either doesn't yet have the
      //   subproject, or has it but it's empty (no clash to worry about).
      // - Explorer: backend pre-flight validates; any default-project choice
      //   is allowed at the UI level.
      return canPublishWorkbooks ? !mustUseCustomLocation : true;
    }
    // Custom location:
    // - Creator: requires a selected project that doesn't already have the
    //   subproject (avoid publish clash).
    // - Explorer: just needs a project picked — backend pre-flight validates
    //   that the workbooks are actually reachable from there.
    if (!selectedProject) return false;
    return canPublishWorkbooks ? !selectedProjectHasAltimateWorkbooks : true;
  }, [
    canPublishWorkbooks,
    error,
    isLoading,
    useCustomLocation,
    mustUseCustomLocation,
    selectedProject,
    selectedProjectHasAltimateWorkbooks,
  ]);

  // Filter projects based on search
  const filteredProjects = searchQuery
    ? projects.filter(
        p =>
          p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.path?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : projects;

  const projectTree = buildProjectTree(filteredProjects);

  // Show full-page loader while fetching projects
  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <div className="al-p-4">
            <DatastoreStepHeader canPublishWorkbooks={canPublishWorkbooks} />

            <Stack direction="row" align="center" justify="center" className="al-min-h-[500px]">
              <ComponentLoader label="Fetching Tableau Projects..." />
            </Stack>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <div className="al-p-4">
          <DatastoreStepHeader />

          <ProjectAlerts
            useCustomLocation={useCustomLocation}
            defaultProjectName={DEFAULT_PROJECT_NAME}
            subprojectName={SUBPROJECT_NAME}
            isError={isError}
            error={error}
            mustUseCustomLocation={mustUseCustomLocation}
            canPublishWorkbooks={canPublishWorkbooks}
            isTableauCloud={isTableauCloud}
          />

          <ProjectLocationSwitch
            useCustomLocation={useCustomLocation}
            onToggle={setUseCustomLocation}
            isDisabled={mustUseCustomLocation || isError}
            mustUseCustomLocation={mustUseCustomLocation}
            subprojectName={SUBPROJECT_NAME}
            canPublishWorkbooks={canPublishWorkbooks}
          />

          {useCustomLocation && (
            <ProjectTreeSection
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              projectTree={projectTree}
              selectedProject={selectedProject}
              onSelectProject={setSelectedProject}
              disabledProjectIds={disabledProjectIds}
              disabledTooltip={
                canPublishWorkbooks
                  ? `This project already contains an ${SUBPROJECT_NAME} subproject`
                  : `This project does not contain the ${SUBPROJECT_NAME} subproject we need to locate`
              }
              error={error}
              onRefresh={refetch}
              isRefreshing={isFetching}
            />
          )}

          {!useCustomLocation && !mustUseCustomLocation && (
            <ProjectInfoDisplay
              type="default"
              defaultProjectName={DEFAULT_PROJECT_NAME}
              canPublishWorkbooks={canPublishWorkbooks}
            />
          )}

          {useCustomLocation && selectedProject && (
            <div className="al-mt-4">
              <ProjectInfoDisplay
                type="selected"
                selectedProject={selectedProject}
                hasAltimateWorkbooks={selectedProjectHasAltimateWorkbooks}
                subprojectName={SUBPROJECT_NAME}
                canPublishWorkbooks={canPublishWorkbooks}
              />
            </div>
          )}

          <ProjectActionButtons
            onBack={handleBack}
            onSave={handleSave}
            canSave={canSave && !isFinishing}
            isSubmitting={isFinishing}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default TableauProjectPicker;
